import { composeCategoryFilteredPages, composeCategoryPickerPage } from "../app/category-pages";
import { shuffleBrowsePages } from "../app/browse-order";
import type { HomeSlot } from "../app/game-registry";
import { composePages, type LauncherSlot } from "../app/launcher-pages";
import type { ExternalGameLink } from "../app/rosebud-catalog";
import type { CatalogCategoryId, CatalogEntry } from "../contracts/catalog";
import type { SystemSurfaceId } from "../contracts/system-surface";
import "./home.css";

const CATEGORY_LABELS: Readonly<Record<CatalogCategoryId, string>> = {
  racing: "Racing",
  sports: "Sports",
  puzzle: "Puzzle",
  action: "Action",
  building: "Building",
  adventure: "Adventure",
  multiplayer: "Multiplayer",
  creative: "Creative",
  "cards-board": "Cards & Board",
  other: "Other",
};

export class HomeView {
  readonly #host: HTMLElement;
  readonly #document: Document;
  // Detached images deliberately keep the browser's first All Games page
  // warm while Categories is on screen. Retaining them for this HomeView's
  // lifetime avoids a garbage-collected speculative request on slow browsers.
  readonly #coverPreloads = new Map<string, HTMLImageElement>();

  constructor(host: HTMLElement, documentScope: Document = document) {
    this.#host = host;
    this.#document = documentScope;
  }

  render(
    slots: readonly HomeSlot[],
    onSelectGame: (gameId: string) => void,
    _onSelectSystemSurface: (surfaceId: SystemSurfaceId) => void = () => undefined,
    catalog: readonly ExternalGameLink[] = [],
    onSelectExternalGame: (link: ExternalGameLink) => void = () => undefined,
    catalogEntries: readonly CatalogEntry[] = [],
  ): void {
    const launcher = this.#document.createElement("section");
    launcher.className = "launcher-pages";
    launcher.setAttribute("aria-label", "Game launcher");
    // The launcher opens on the category picker. Categories owns the
    // top-left slot in every state; its next tap swaps to all games.
    let mode: "games" | "categories" = "categories";
    let activeCategory: CatalogCategoryId | null = null;
    let pageIndex = 0;
    let pagePickerOpen = false;

    const allPages = composePages(slots, catalog);
    const initialAllPages = shuffleBrowsePages(allPages);
    let browsePages = initialAllPages;
    let allGamesOpened = false;
    const previousPages = new Map<string, readonly (readonly LauncherSlot[])[]>();

    // A view owns one shuffled snapshot. Page arrows and the page picker must
    // never draw again: doing so duplicates/skips games while the user browses.
    const currentPages = (): readonly (readonly LauncherSlot[])[] =>
      mode === "categories" ? [composeCategoryPickerPage()] : browsePages;

    const preloadCover = (url: string): void => {
      if (!url || this.#coverPreloads.has(url)) return;
      const image = this.#document.createElement("img");
      image.setAttribute("loading", "eager");
      image.setAttribute("fetchpriority", "high");
      image.src = url;
      this.#coverPreloads.set(url, image);
    };

    /**
     * A progressive JPEG can paint one strip at a time. On the first launcher
     * visit that looked like an icon had been cropped or was sitting in a
     * broken dark card. Keep the slot's geometry, but reveal its art only
     * once the complete same-origin image has arrived.
     */
    const attachCompleteLocalCover = (tile: HTMLElement, image: HTMLImageElement, source: string): void => {
      tile.className += " game-tile--cover-pending";
      image.setAttribute("loading", "eager");
      image.setAttribute("fetchpriority", "high");
      const reveal = (): void => {
        tile.className = tile.className.replace(" game-tile--cover-pending", "");
      };
      image.addEventListener("load", reveal, { once: true });
      image.addEventListener("error", () => { image.className += " game-cover--failed"; }, { once: true });
      image.src = source;
      if (image.complete && image.naturalWidth > 0) reveal();
    };

    const prewarmAllGames = (): void => {
      // Categories is intentionally the first page. Its one-tap All Games
      // counterpart should nevertheless have its visible images in flight
      // already, rather than briefly becoming a dozen empty hard-edged cards.
      for (const slot of initialAllPages[0] ?? []) {
        if (slot.kind === "local") preloadCover(slot.manifest.coverAsset);
        if (slot.kind === "external") preloadCover(slot.game.coverUrl);
      }
    };

    const openCategoryPicker = (): void => {
      // Toggle between the categories picker and the randomized all-games grid.
      // activeCategory is always cleared here: the picker never claims to be
      // "Showing X" while displaying every category, and the all-games grid
      // it toggles to is genuinely unfiltered, not a leftover filtered view.
      mode = mode === "categories" ? "games" : "categories";
      if (mode === "games") {
        browsePages = allGamesOpened
          ? shuffleBrowsePages(allPages, previousPages.get("all"))
          : initialAllPages;
        allGamesOpened = true;
        previousPages.set("all", browsePages);
      }
      activeCategory = null;
      pageIndex = 0;
      pagePickerOpen = false;
      renderPage();
    };

    const selectCategoryOption = (category: CatalogCategoryId): void => {
      activeCategory = category;
      browsePages = shuffleBrowsePages(
        composeCategoryFilteredPages(catalogEntries, category, catalog),
        previousPages.get(category),
      );
      previousPages.set(category, browsePages);
      mode = "games";
      pageIndex = 0;
      pagePickerOpen = false;
      renderPage();
    };

    const launchCatalogEntry = (entry: CatalogEntry): void => {
      if (entry.kind === "first-party" || entry.kind === "hosted") {
        onSelectGame(entry.launch.gameId);
        return;
      }
      if (entry.kind === "external") {
        onSelectExternalGame({
          slug: entry.launch.slug,
          title: entry.title,
          // Every external CatalogEntry today is source: "rosebud", so this
          // narrowing is currently exact; a future non-Rosebud external
          // source needs ExternalOverlay's own launch shape generalized,
          // not a wider cast here.
          url: entry.launch.url as `https://rosebud.ai/play/${string}`,
          coverUrl: entry.cover.status === "present" ? entry.cover.url : "",
          // Without this, every Categories-tile launch silently lost the
          // harvested gateway origin and fell back to Rosebud's own chrome
          // -- the actual cause of "works from one tile, not the other."
          ...(entry.launch.gatewayUrl
            ? { gatewayUrl: entry.launch.gatewayUrl as `https://${string}.run.app/api/server/${string}/index.html` }
            : {}),
        });
      }
      // "creator-release" has no public launch route yet (Market is a later
      // phase); nothing tags that kind today, so this is unreachable in
      // practice, not a silently missing feature.
    };

    const renderSlot = (slot: LauncherSlot, index: number): HTMLElement => {
      const launcherPosition = String(index + 1);

      if (slot.kind === "create") {
        const create = this.#document.createElement("a");
        create.className = "game-tile system-entry create-entry";
        create.setAttribute("href", "https://builder.puter.com/");
        create.setAttribute("target", "_self");
        create.setAttribute("rel", "external");
        create.dataset.systemSurfaceId = "create";
        create.dataset.launcherPosition = launcherPosition;
        create.setAttribute("aria-label", "Create a game with Puter");
        // Native same-tab navigation: no popup, iframe, internal creator,
        // account creation, API key, or owner-paid inference in this path.

        const createIcon = this.#document.createElement("img");
        createIcon.className = "game-cover";
        createIcon.alt = "";
        createIcon.setAttribute("draggable", "false");
        attachCompleteLocalCover(create, createIcon, "/system/create.jpg");
        create.append(createIcon);
        return create;
      }

      if (slot.kind === "categories") {
        const isActive = mode === "categories" || activeCategory !== null;
        const tile = this.#document.createElement("button");
        tile.className = isActive
          ? "game-tile system-entry categories-entry categories-entry--active"
          : "game-tile system-entry categories-entry";
        tile.type = "button";
        tile.dataset.systemSurfaceId = "categories";
        tile.dataset.launcherPosition = launcherPosition;
        // Three real states: filtered by a category (state-oriented label),
        // sitting on the categories landing page (one tap opens all games),
        // or sitting on all games (one tap returns to Categories).
        tile.setAttribute(
          "aria-label",
          activeCategory
            ? `Categories. Showing ${CATEGORY_LABELS[activeCategory]}.`
            : mode === "categories"
              ? "All games"
              : "Categories",
        );
        tile.addEventListener("click", openCategoryPicker);

        const icon = this.#document.createElement("img");
        icon.className = "categories-icon";
        icon.alt = "";
        icon.setAttribute("draggable", "false");
        attachCompleteLocalCover(tile, icon, "/system/categories.jpg");
        tile.append(icon);
        return tile;
      }

      if (slot.kind === "category-option") {
        const label = CATEGORY_LABELS[slot.category];
        const tile = this.#document.createElement("button");
        tile.className = "game-tile game-entry category-option-entry";
        tile.type = "button";
        tile.dataset.category = slot.category;
        tile.dataset.launcherPosition = launcherPosition;
        tile.setAttribute("aria-label", label);
        tile.addEventListener("click", () => selectCategoryOption(slot.category));

        const icon = this.#document.createElement("img");
        icon.className = "game-cover";
        icon.alt = "";
        icon.setAttribute("draggable", "false");
        attachCompleteLocalCover(tile, icon, `/system/categories/${slot.category}.jpg`);

        tile.append(icon);
        return tile;
      }

      if (slot.kind === "catalog-entry") {
        const { entry } = slot;
        const isExternal = entry.kind === "external";
        const tile = this.#document.createElement(isExternal ? "a" : "button");
        tile.className = "game-tile game-entry catalog-entry-tile";
        tile.dataset.catalogId = entry.catalogId;
        tile.dataset.launcherPosition = launcherPosition;
        tile.setAttribute("aria-label", entry.title);
        tile.setAttribute("title", entry.title);
        if (isExternal && entry.kind === "external") {
          tile.setAttribute("href", entry.launch.url);
          tile.addEventListener("click", (event) => { event.preventDefault(); launchCatalogEntry(entry); });
        } else {
          (tile as HTMLButtonElement).type = "button";
          tile.addEventListener("click", () => launchCatalogEntry(entry));
        }

        if (entry.cover.status === "present") {
          tile.className += " game-tile--cover-pending";
          const cover = this.#document.createElement("img");
          cover.className = "game-cover";
          cover.alt = "";
          // This image occupies a visible, immediately interactive launcher
          // tile. Ask for it now so changing pages never presents a grid of
          // dark empty rectangles while the next set of covers is deferred.
          cover.setAttribute("loading", "eager");
          cover.setAttribute("fetchpriority", "high");
          cover.setAttribute("draggable", "false");
          cover.addEventListener("load", () => {
            tile.className = tile.className.replace(" game-tile--cover-pending", "");
          }, { once: true });
          cover.addEventListener("error", () => { cover.className += " game-cover--failed"; }, { once: true });
          cover.src = entry.cover.url;
          tile.append(cover);
        } else {
          tile.className += " game-cover--failed";
          const fallbackTitle = this.#document.createElement("span");
          fallbackTitle.className = "game-tile__title-fallback";
          fallbackTitle.textContent = entry.title;
          tile.append(fallbackTitle);
        }
        return tile;
      }

      if (slot.kind === "placeholder") {
        const placeholder = this.#document.createElement("div");
        placeholder.className = "game-tile game-placeholder";
        placeholder.dataset.slotKey = slot.key;
        placeholder.dataset.launcherPosition = launcherPosition;
        placeholder.setAttribute("aria-hidden", "true");
        return placeholder;
      }

      if (slot.kind === "external") {
        const anchor = this.#document.createElement("a");
        anchor.className = "game-tile game-entry external-entry game-tile--cover-pending";
        anchor.dataset.externalGameSlug = slot.game.slug;
        anchor.dataset.launcherPosition = launcherPosition;
        anchor.setAttribute("href", slot.game.url);
        anchor.setAttribute("aria-label", slot.game.title);
        anchor.setAttribute("title", slot.game.title);
        // preventDefault stops the native same-tab navigation. Opening the
        // overlay from this same synchronous click keeps the tap's
        // user-gesture activation alive on Carcade's own document, which
        // fullscreen requires -- that activation cannot survive a real page
        // navigation to another origin.
        anchor.addEventListener("click", (event) => {
          event.preventDefault();
          onSelectExternalGame(slot.game);
        });

        const cover = this.#document.createElement("img");
        cover.className = "game-cover";
        cover.alt = "";
        // See catalog-entry above: each rendered page is at most eleven
        // covers, so eagerly loading the current page prevents an ugly blank
        // tile state without preloading the entire external catalogue.
        cover.setAttribute("loading", "eager");
        cover.setAttribute("fetchpriority", "high");
        cover.setAttribute("draggable", "false");
        cover.addEventListener("load", () => {
          anchor.className = anchor.className.replace(" game-tile--cover-pending", "");
        }, { once: true });
        cover.addEventListener("error", () => { cover.className += " game-cover--failed"; }, { once: true });
        cover.src = slot.game.coverUrl;
        anchor.append(cover);
        return anchor;
      }

      const { manifest } = slot;
      const button = this.#document.createElement("button");
      button.className = "game-tile game-entry";
      button.type = "button";
      button.dataset.gameId = manifest.id;
      button.dataset.status = manifest.status;
      button.dataset.launcherPosition = launcherPosition;
      button.setAttribute("aria-label", manifest.title);

      const isPlayable = manifest.status === "playable";
      button.disabled = !isPlayable;
      if (!isPlayable) {
        button.className += " game-entry--disabled";
        button.setAttribute("aria-disabled", "true");
      } else {
        button.addEventListener("click", () => onSelectGame(manifest.id));
      }

      const cover = this.#document.createElement("img");
      cover.className = "game-cover";
      cover.src = manifest.coverAsset;
      cover.alt = "";
      cover.setAttribute("draggable", "false");
      button.append(cover);
      return button;
    };

    const renderPage = (): void => {
      const pages = currentPages();
      if (pageIndex >= pages.length) pageIndex = 0;
      launcher.dataset.launcherPage = String(pageIndex + 1);
      launcher.dataset.launcherMode = mode;
      const grid = this.#document.createElement("div");
      grid.className = "game-grid";
      grid.setAttribute("aria-label", mode === "categories" ? "Categories" : "Games");
      grid.append(...(pages[pageIndex] ?? []).map(renderSlot));

      const controls: HTMLElement[] = [];
      if (pageIndex > 0) {
        const previous = this.#document.createElement("button");
        previous.className = "launcher-page-control launcher-page-control--previous";
        previous.type = "button";
        previous.setAttribute("aria-label", "Previous launcher page");
        previous.textContent = "‹";
        previous.addEventListener("click", () => { pageIndex -= 1; pagePickerOpen = false; renderPage(); });
        controls.push(previous);
      }
      if (pageIndex < pages.length - 1) {
        const next = this.#document.createElement("button");
        next.className = "launcher-page-control launcher-page-control--next";
        next.type = "button";
        next.setAttribute("aria-label", "Next launcher page");
        next.textContent = "›";
        next.addEventListener("click", () => { pageIndex += 1; pagePickerOpen = false; renderPage(); });
        controls.push(next);
      }

      const pageChrome: HTMLElement[] = [];
      if (pages.length > 1) {
        const readout = this.#document.createElement("button");
        readout.className = pagePickerOpen ? "launcher-page-readout launcher-page-readout--open" : "launcher-page-readout";
        readout.type = "button";
        readout.textContent = `${pageIndex + 1} / ${pages.length}`;
        readout.setAttribute("aria-label", `Page ${pageIndex + 1} of ${pages.length}. Tap to choose a page.`);
        readout.addEventListener("click", () => { pagePickerOpen = !pagePickerOpen; renderPage(); });
        pageChrome.push(...controls, readout);

        if (pagePickerOpen) {
          const picker = this.#document.createElement("div");
          picker.className = "launcher-page-picker";
          picker.setAttribute("role", "menu");
          picker.setAttribute("aria-label", "Choose a page");
          const pickerGrid = this.#document.createElement("div");
          pickerGrid.className = "launcher-page-picker__grid";
          for (let index = 0; index < pages.length; index += 1) {
            const pageButton = this.#document.createElement("button");
            pageButton.type = "button";
            pageButton.className = index === pageIndex
              ? "launcher-page-picker__button launcher-page-picker__button--current"
              : "launcher-page-picker__button";
            pageButton.textContent = String(index + 1);
            pageButton.setAttribute("aria-label", `Page ${index + 1}`);
            pageButton.addEventListener("click", () => { pageIndex = index; pagePickerOpen = false; renderPage(); });
            pickerGrid.append(pageButton);
          }
          picker.append(pickerGrid);
          pageChrome.push(picker);
        }
      }

      launcher.replaceChildren(grid, ...pageChrome);
    };

    prewarmAllGames();
    renderPage();
    this.#host.replaceChildren(launcher);
  }

  destroy(): void {
    this.#coverPreloads.clear();
    this.#host.replaceChildren();
  }
}
